import type {
  AxfoodBasicProductViewModel,
  AxfoodOfflinePromotionViewModel,
  AxfoodPromotionViewModel,
} from '@occ/api-client';

type Product = AxfoodBasicProductViewModel | undefined;

// next-intl 'product'-namespace translator, narrowed to the only keys this util renders.
type ComparePriceTranslate = (key: 'price.compare' | 'price.compare-missing') => string;

const getPromotion = (product: Product) => product?.potentialPromotions?.[0];

/**
 * The promotion's compare price with a unit appended when missing.
 * Ex: '16,00 kr' + unit 'kg' → '16,00 kr/kg'; an already-'/'-qualified value passes through.
 */
export const getPromotionComparePrice = (product: Product): string => {
  const promotion = getPromotion(product);
  if (promotion?.comparePrice && promotion.comparePrice.indexOf('/') > -1) return promotion.comparePrice;
  if (promotion?.comparePrice && product?.comparePriceUnit)
    return `${promotion.comparePrice}/${product.comparePriceUnit}`;
  return '';
};

/** e.g. "Jmf-pris 19,90 kr/kg", or the "missing" label when there's no compare price. */
export const formatComparePrice = (product: Product, t: ComparePriceTranslate): string => {
  const hasComparePrice = !!(product?.comparePrice !== '' && product?.comparePriceUnit);
  return hasComparePrice
    ? `${t('price.compare')} ${product?.comparePrice}/${product?.comparePriceUnit}`
    : t('price.compare-missing');
};

/** Like formatComparePrice, but for the promotion's compare price. */
export const formatPromotionComparePrice = (product: Product, t: ComparePriceTranslate): string => {
  const promotionComparePrice = getPromotionComparePrice(product);
  return promotionComparePrice ? `${t('price.compare')} ${promotionComparePrice}` : t('price.compare-missing');
};

/**
 * Whether the product's price is the lowest it has had, which is when a promotion saving may be shown.
 * Also true when there's no lowest historical price to compare against.
 */
export const isAtLowestHistoricalPrice = (product: {
  potentialPromotions?: (AxfoodPromotionViewModel | AxfoodOfflinePromotionViewModel)[];
  priceValue?: number;
}): boolean => {
  const promotion = product.potentialPromotions?.[0];
  const lowestHistoricalPrice =
    promotion && 'lowestHistoricalPrice' in promotion ? promotion.lowestHistoricalPrice : undefined;
  return !lowestHistoricalPrice || lowestHistoricalPrice.value === product.priceValue;
};
